/**
 * WebHID 授权与 open() 守卫：仅用户点击「连接设备 / 添加新设备」时可 requestDevice / open 未授权设备。
 * 固件升级进 boot 后 SDK 会调 requestDevice；无用户手势时改为返回 getDevices 已授权设备。
 * 本文件禁止 @/ 别名引用，子应用 webpack 会编译 main-app 源码且 @ 指向子应用 src。
 */

import { isKeyboardFirmwareUpgradeInProgress } from "@eweadn-qiankun/shared-utils";

/** 与 hidGlobalStore.SKIP_HOME_HID_AUTOCONNECT_ONCE_KEY 保持一致 */
export const SKIP_HOME_HID_AUTOCONNECT_ONCE_KEY =
  "eweadn:skipHomeHidAutoconnectOnce";

declare global {
  interface Window {
    /** 子应用回首页：同步抑制 autoconnect（早于 sessionStorage 异步 effect） */
    __eweadnSuppressHomeHidAutoconnect?: boolean;
  }
}

let userInitiatedHidConnect = false;
let webHidAuthorizationGuardInstalled = false;

export function markUserInitiatedHidConnect(): void {
  userInitiatedHidConnect = true;
}

export function clearUserInitiatedHidConnect(): void {
  userInitiatedHidConnect = false;
}

export function isUserInitiatedHidConnect(): boolean {
  return userInitiatedHidConnect;
}

export function isSkipHomeHidAutoconnectPending(): boolean {
  try {
    if (
      typeof window !== "undefined" &&
      window.__eweadnSuppressHomeHidAutoconnect === true
    ) {
      return true;
    }
    return (
      typeof window !== "undefined" &&
      window.sessionStorage.getItem(SKIP_HOME_HID_AUTOCONNECT_ONCE_KEY) === "1"
    );
  } catch {
    return false;
  }
}

/** 子应用点「设备列表」回首页前调用，同步拉高抑制（避免竞态弹窗） */
export function armHomeHidAutoconnectSuppress(): void {
  if (typeof window === "undefined") return;
  window.__eweadnSuppressHomeHidAutoconnect = true;
  try {
    window.sessionStorage.setItem(SKIP_HOME_HID_AUTOCONNECT_ONCE_KEY, "1");
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new Event("eweadn:suppressHomeHidAutoconnect"));
  } catch {
    /* ignore */
  }
}

export function disarmHomeHidAutoconnectSuppress(): void {
  if (typeof window === "undefined") return;
  window.__eweadnSuppressHomeHidAutoconnect = false;
  try {
    window.sessionStorage.removeItem(SKIP_HOME_HID_AUTOCONNECT_ONCE_KEY);
  } catch {
    /* ignore */
  }
}

/** 子应用回首页 / 热插拔 / autoconnect：默认禁止 requestDevice，仅用户点击「连接设备」时允许 */
export function canInvokeHidRequestDevice(): boolean {
  if (isUserInitiatedHidConnect()) return true;
  if (isSkipHomeHidAutoconnectPending()) {
    console.log(
      "[webHidAuth] 子应用回首页抑制中，跳过 navigator.hid.requestDevice"
    );
  } else {
    console.log(
      "[webHidAuth] 非用户点击连接，跳过 navigator.hid.requestDevice"
    );
  }
  return false;
}

/** 升级中无用户手势：用已授权 getDevices 顶替 requestDevice，避免 Boot 后概率性 noDevice */
export async function getAuthorizedHidDevicesForUpgradeRequest(
  options?: HIDDeviceRequestOptions
): Promise<any[]> {
  const authorized = await getAuthorizedHidDevices();
  if (!authorized.length) return [];
  const filters = options?.filters;
  if (!Array.isArray(filters) || filters.length === 0) return authorized;
  return authorized.filter((device) =>
    filters.some((filter: any) => {
      if (filter?.vendorId != null && device?.vendorId !== filter.vendorId) {
        return false;
      }
      if (filter?.productId != null && device?.productId !== filter.productId) {
        return false;
      }
      if (
        filter?.usagePage != null &&
        !device?.collections?.some(
          (c: any) => c?.usagePage === filter.usagePage
        )
      ) {
        return false;
      }
      if (
        filter?.usage != null &&
        !device?.collections?.some((c: any) => c?.usage === filter.usage)
      ) {
        return false;
      }
      return true;
    })
  );
}

/** 带守卫的 requestDevice：仅用户点击「连接设备 / 添加新设备」时可弹窗 */
export async function safeRequestHidDevice(options: {
  filters?: unknown[];
}): Promise<any[]> {
  if (!canInvokeHidRequestDevice()) {
    if (isKeyboardFirmwareUpgradeInProgress()) {
      return getAuthorizedHidDevicesForUpgradeRequest(
        options as HIDDeviceRequestOptions
      );
    }
    return [];
  }
  if (typeof navigator === "undefined" || !("hid" in navigator)) {
    return [];
  }
  try {
    return await (navigator as any).hid.requestDevice(options);
  } catch {
    return [];
  }
}

export function hidPhysicalKey(device: any): string {
  if (!device) return "";
  return `${device.vendorId ?? ""}-${device.productId ?? ""}-${String(device.serialNumber ?? "").trim()}`;
}

export async function getAuthorizedHidDevices(): Promise<any[]> {
  if (typeof navigator === "undefined" || !("hid" in navigator)) {
    return [];
  }
  try {
    return await (navigator as any).hid.getDevices();
  } catch {
    return [];
  }
}

export function isHidDeviceGranted(device: any, authorized?: any[]): boolean {
  if (!device) return false;
  const list = authorized ?? null;
  const check = (auth: any[]) =>
    auth.some(
      (d: any) =>
        d === device ||
        (d?.vendorId === device?.vendorId &&
          d?.productId === device?.productId &&
          String(d?.serialNumber ?? "").trim() ===
            String(device?.serialNumber ?? "").trim())
    );
  if (list) return check(list);
  return false;
}

export async function isHidDeviceGrantedAsync(device: any): Promise<boolean> {
  if (!device) return false;
  const authorized = await getAuthorizedHidDevices();
  return isHidDeviceGranted(device, authorized);
}

/**
 * 仅用户点击「添加设备」时可调用；热插拔 / rescan 等场景一律返回空数组。
 * 固件升级中例外：回退已授权 getDevices，供 Boot 重连。
 */
export async function safeRequestHidDevices(
  options?: HIDDeviceRequestOptions
): Promise<any[]> {
  if (typeof navigator === "undefined" || !("hid" in navigator)) {
    return [];
  }
  if (!isUserInitiatedHidConnect()) {
    if (isKeyboardFirmwareUpgradeInProgress()) {
      return getAuthorizedHidDevicesForUpgradeRequest(options);
    }
    console.log(
      "[webHidAuth] 已拦截非用户触发的 requestDevice（热插拔/rescan 不会弹授权框）"
    );
    return [];
  }
  try {
    return await (navigator as any).hid.requestDevice(options);
  } catch {
    return [];
  }
}

/**
 * 在应用最早阶段安装：拦截 navigator.hid.requestDevice 与 HIDManager.requestDevice，
 * 防止 SDK / 热插拔链路误触发浏览器 HID 选择弹窗。
 */
export function installWebHidAuthorizationGuard(): void {
  if (webHidAuthorizationGuardInstalled) return;
  if (typeof navigator === "undefined" || !("hid" in navigator)) return;

  const hid = (navigator as any).hid;
  if (typeof hid.requestDevice === "function") {
    const nativeRequestDevice = hid.requestDevice.bind(hid);
    hid.requestDevice = async (options?: HIDDeviceRequestOptions) => {
      if (!isUserInitiatedHidConnect()) {
        if (isKeyboardFirmwareUpgradeInProgress()) {
          console.log(
            "[webHidAuth] 固件升级中：requestDevice 回退为 getDevices 已授权设备"
          );
          return getAuthorizedHidDevicesForUpgradeRequest(options);
        }
        console.log(
          "[webHidAuth] 已拦截 navigator.hid.requestDevice（非用户点击添加设备）"
        );
        return [];
      }
      return nativeRequestDevice(options);
    };
  }

  webHidAuthorizationGuardInstalled = true;

  void import("@bytech/web-sdk-machine")
    .then((mod) => {
      const HIDManager = mod?.HIDManager as
        | {
            requestDevice?: (...args: unknown[]) => Promise<unknown>;
            __eweadnRequestDeviceGuardInstalled?: boolean;
          }
        | undefined;
      if (
        !HIDManager ||
        typeof HIDManager.requestDevice !== "function" ||
        HIDManager.__eweadnRequestDeviceGuardInstalled
      ) {
        return;
      }
      const nativeHidManagerRequest = HIDManager.requestDevice.bind(HIDManager);
      HIDManager.requestDevice = async (...args: unknown[]) => {
        if (!isUserInitiatedHidConnect()) {
          console.log(
            "[webHidAuth] 已拦截 HIDManager.requestDevice（非用户点击添加设备）"
          );
          return [];
        }
        return nativeHidManagerRequest(...args);
      };
      HIDManager.__eweadnRequestDeviceGuardInstalled = true;
    })
    .catch(() => {
      /* SDK 未加载时忽略 */
    });
}

/** 非用户点击场景下，未授权设备禁止 open()，避免浏览器反复弹出 HID 选择框 */
export async function safeOpenHidDevice(device: any): Promise<boolean> {
  if (!device?.open) return false;
  if (device.opened) return true;
  if (!isUserInitiatedHidConnect()) {
    const granted = await isHidDeviceGrantedAsync(device);
    if (!granted) {
      if (isSkipHomeHidAutoconnectPending()) {
        console.log(
          "[webHidAuth] 子应用回首页抑制中，跳过未授权设备的 device.open()"
        );
      } else {
        console.log("[webHidAuth] 跳过 open()：设备未授权且非用户点击连接按钮");
      }
      return false;
    }
  }
  const maxAttempts = 6;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (device.opened) return true;
    try {
      await device.open();
      return !!device.opened;
    } catch (e) {
      if (device.opened) return true;
      const msg = e instanceof Error ? e.message : String(e);
      if (/already open/i.test(msg)) return true;
      if (/in progress/i.test(msg) && attempt < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, 40 * (attempt + 1)));
        continue;
      }
      console.warn("[webHidAuth] device.open() 失败:", e);
      return false;
    }
  }
  return !!device.opened;
}
